import {
  ROUTINES_DESKTOP_NURTURE_BANNER_COUNT_THRESHOLD,
  routinesDesktopNurtureBannerDismissedAtom,
} from '@/atoms/routinesDesktopNurtureAtom';
import { useAllWorkflowRuns } from '@/features/workflows/hooks';
import { getRegion } from '@/helpers/getRegion';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsWorkDesktopNurtureBannerEnabled } from '@/hooks/useIsWorkDesktopNurtureBannerEnabled';
import {
  resolveWorkBillingAnalyticsProps,
  trackWorkUpgradeActioned,
} from '@/models/analytics/features/work';
import {
  RoutinesDesktopNurtureBanner as RoutinesDesktopNurtureBannerView,
  type RoutinesDesktopNurtureBannerProps,
} from '@heidi/agents';
import type {
  TrackWorkDesktopNurtureBannerEvent,
  TrackWorkUpgradeActioned,
} from '@heidi/agents/contracts';
import { track as trackViaPipeline } from '@heidi/runtime/analytics';
import { useAtom } from 'jotai';

const trackNurtureEvent: TrackWorkDesktopNurtureBannerEvent = (eventName, props) => {
  trackViaPipeline({
    event_name: eventName,
    props: {
      surface: 'work_home',
      region: getRegion(),
      ...props,
    },
    options: { amplitude: false, posthog: false },
  });
};

const trackUpgradeActioned: TrackWorkUpgradeActioned = (props) =>
  trackWorkUpgradeActioned({ ...props, ...resolveWorkBillingAnalyticsProps() });

/**
 * Binds the Work-home desktop nurture banner to the app: its kill switch, its
 * persisted dismissal, the routine-run and account reads, and the emits.
 */
export const RoutinesDesktopNurtureBanner = (
  props: RoutinesDesktopNurtureBannerProps
) => {
  const { data } = useAllWorkflowRuns();
  const { data: user } = useQueryUser((profile) => ({
    is_managed: profile.is_managed,
    created_at: profile.created_at,
  }));
  const [dismissed, setDismissed] = useAtom(routinesDesktopNurtureBannerDismissedAtom);
  const isEnabled = useIsWorkDesktopNurtureBannerEnabled();
  return (
    <RoutinesDesktopNurtureBannerView
      {...props}
      isEnabled={isEnabled}
      dismissed={dismissed}
      setDismissed={setDismissed}
      runCount={data?.total ?? 0}
      minimumRunCount={ROUTINES_DESKTOP_NURTURE_BANNER_COUNT_THRESHOLD}
      isManaged={Boolean(user?.is_managed)}
      accountCreatedAt={user?.created_at}
      trackNurtureEvent={trackNurtureEvent}
      trackUpgradeActioned={trackUpgradeActioned}
    />
  );
};
